import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, Output, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import type { OEPNavItem } from 'agu_oep_core';
import { OEPViewBase } from '../state/oep-view-base';

/**
 * The navy band (markup.html:131-165): AGU wordmark, program name, notifications bell with the
 * unread count (MJNotificationService.unreadCount$), identity chip, and the role-derived nav row
 * (agu_oep_core BuildNav via OEPContextService). The identity chip opens the exit menu — Sign out
 * always, Back to MJ Explorer when the user holds another app — which closes the
 * immersive-surface trap for a user whose only app is OEP.
 */
@Component({
    standalone: false,
    selector: 'oep-header',
    templateUrl: './oep-header.component.html',
    styleUrl: './oep-header.component.scss',
})
export class OEPHeaderComponent extends OEPViewBase implements OnDestroy {
    @Output() OpenNotifications = new EventEmitter<void>();
    MenuOpen = false;
    AccountOpen = false;
    Unread = 0;
    private unreadSub: Subscription | null = null;
    private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

    protected async Load(): Promise<void> {
        this.unreadSub?.unsubscribe();
        this.unreadSub = MJNotificationService.Instance.unreadCount$.subscribe((n) => {
            this.Unread = n;
            this.Changed();
        });
    }

    override ngOnDestroy(): void {
        this.unreadSub?.unsubscribe();
        this.unreadSub = null;
        super.ngOnDestroy();
    }

    get Nav(): OEPNavItem[] { return this.Context.Nav; }
    get Active(): string { return this.Host.CurrentLocation.View; }
    /** logic.js:506 notifAria. */
    get BellLabel(): string { return `Notifications, ${this.Unread} unread`; }
    /** logic.js:501 menuLabel: glyph + the current screen's label. */
    get MenuLabel(): string {
        const current = this.Nav.find((n) => n.View === this.Active) ?? this.Nav[0];
        return `${this.MenuOpen ? '✕' : '☰'}  ${current?.Label ?? ''}`;
    }

    Pick(item: OEPNavItem): void {
        this.MenuOpen = false;
        this.Go(item.View);
    }
    ToggleAccount(): void { this.AccountOpen = !this.AccountOpen; }

    /** A click anywhere outside the account disclosure closes it (Escape also does, in the template). */
    @HostListener('document:click', ['$event.target'])
    OnDocumentClick(target: EventTarget | null): void {
        if (!this.AccountOpen) return;
        const account = this.hostEl.nativeElement.querySelector('.oep-band__account');
        if (target instanceof Node && account?.contains(target)) return;
        this.AccountOpen = false;
        this.Changed();
    }
    CloseAccount(): void { this.AccountOpen = false; }
    async SignOut(): Promise<void> {
        this.AccountOpen = false;
        await this.Host.SignOut();
    }
    ReturnToHost(): void {
        this.AccountOpen = false;
        this.Host.ReturnToHost();
    }
}
